{
  "header": {
    "nav": {
      "services": "Services",
      "products": "Products",
      "security": "Security",
      "process": "Process",
      "contact": "Contact"
    },
    "cta": "Get in touch",
    "brand_aria": "Niertech Solutions",
    "nav_aria": "Primary"
  },
  "hero": {
    "eyebrow": "Niertech Solutions",
    "title": "Software development and IT consulting for teams that won't accept improvisation.",
    "subtitle": "We deliver custom systems, infrastructure, information security, and data intelligence — with the rigor that enterprise and public-sector projects demand.",
    "cta_primary": "Talk to the team",
    "cta_secondary": "Our services",
    "stack_aria": "Stack"
  },
  "pillars": {
    "eyebrow": "Our services",
    "title": "Tech from scope to deploy — under one roof.",
    "lead": "We're a tech company focused on delivering high-quality solutions to companies across industries, with the reliability serious projects require.",
    "items": {
      "consulting": {
        "title": "IT Consulting",
        "body": "Strategic advisory to optimize processes, evaluate stack, and steer technology decisions — from roadmap to operating model."
      },
      "dev": {
        "title": "Software Development",
        "body": "Custom web and mobile systems built on modern stacks. From functional scope to production delivery, with documentation and tests."
      },
      "infra": {
        "title": "IT Infrastructure",
        "body": "Cloud computing, servers, networking, and provisioning. Scalable architecture, monitoring, backups, and operational continuity."
      },
      "outsourcing": {
        "title": "IT Outsourcing",
        "body": "Specialized professionals allocated to short- or long-term projects — with technical management and defined SLAs."
      },
      "security": {
        "title": "Information Security",
        "body": "Data protection aligned with LGPD, GDPR, and international standards. Policies, audits, access control, and incident response."
      },
      "bi": {
        "title": "Business Intelligence",
        "body": "Dashboards, reports, and integrations that turn data into strategic insight. Actionable operational indicators, not just charts."
      }
    }
  },
  "projects": {
    "eyebrow": "Products",
    "title": "Real systems, in production.",
    "lead": "Two flagship products focused on the public sector, supported by a line of corporate applications covering CRM, communication, and data intelligence.",
    "flagships": {
      "siplanp": {
        "tag": "Public-Sector Management",
        "name": "SIPLANP",
        "subtitle": "Public Planning & Management System",
        "desc": "Multi-tenant SaaS platform built for government agencies replacing spreadsheets and fragmented processes. Kanban, dynamic forms, internal chat, calendar, and vertical modules attached as the bid requires.",
        "features": [
          "Multi-tenant with per-client isolation",
          "Role- and department-based access control",
          "Pluggable vertical modules (engineering, catalog, workflows)",
          "Export, audit, and full activity logs",
          "Responsive web interface, no install"
        ]
      },
      "ntnutri": {
        "tag": "School Nutrition",
        "name": "NtNutri",
        "subtitle": "School Nutrition & Food Management",
        "desc": "SaaS aligned with Brazil's FNDE Resolution and PNAE Law, ready for electronic bid waivers and public tenders. Plans menus, computes quantities, consolidates bid lists, manages inventory, and produces FNDE accountability reports without re-entry.",
        "features": [
          "Menu planning with per-capita serving",
          "Automatic quantity computation",
          "Consolidated bid lists",
          "Inventory balances and food movements",
          "30% family-farming sourcing (Law 11.947/2009)",
          "FNDE export and management reports",
          "LGPD compliance, role profiles, active transparency"
        ]
      }
    },
    "products": {
      "nierbusyness": {
        "tag": "Corporate CRM",
        "name": "NierBusyness",
        "desc": "End-to-end CRM for the commercial routine: lead capture and qualification, sales pipeline, proposals, contracts, digital signatures, and post-sale follow-up. Integrates with ERP, email, and WhatsApp to centralize every customer touchpoint."
      },
      "yorwha": {
        "tag": "Multi-Channel Inbox",
        "name": "Yorwha",
        "desc": "Multi-channel messaging platform for SMBs: WhatsApp Business, Instagram Direct, Facebook Messenger, TikTok DM, SMS, email, and Telegram in a single inbox. Queues, RBAC, MFA (TOTP), templates, reactions, attachments, and calls. GDPR/LGPD/CPRA compliant — credentials encrypted at rest (AES-256-GCM), data export and deletion endpoints, formal incident-response and access-control policies."
      },
      "magnum": {
        "tag": "Analytics & SCADA",
        "name": "Magnum",
        "desc": "Real-time analytics and supervisory platform. Executive dashboards, operational KPIs, rule-based alerts, integrations across heterogeneous sources (SQL, APIs, CSV, IoT), and consolidated history for data-driven decisions — from the shop floor to the boardroom."
      }
    }
  },
  "security": {
    "eyebrow": "Security & Compliance",
    "title": "Security isn't a checklist — it's how we build.",
    "lead": "Every system we ship is defensive by default: encryption at rest, multi-factor auth, role-based access control, and formal privacy / retention / incident-response policies. We engage with security researchers and run a responsible-disclosure program.",
    "items": {
      "encryption": {
        "title": "Encryption at rest + in transit",
        "body": "AES-256-GCM on credentials and sensitive columns; TLS 1.3 end-to-end at the edge. Keys held in environment variables, never in the database."
      },
      "mfa": {
        "title": "Mandatory MFA + RBAC",
        "body": "TOTP (RFC 6238) enforced for owner, admin, and developer roles. Role-based access control with least-privilege principles, reviewed annually."
      },
      "compliance": {
        "title": "GDPR / LGPD / CPRA aligned",
        "body": "Public privacy notice, data export and deletion endpoints (subject rights), minimal per-class retention, provider deauth processed within 24 hours."
      },
      "scans": {
        "title": "Continuous scanning",
        "body": "OWASP ZAP + Nuclei quarterly and on each material release. Dependabot active. Clear SLAs: critical 7d, high 14d, medium 30d."
      },
      "disclosure": {
        "title": "Responsible disclosure",
        "body": "Public SECURITY.md with a 90-day fix window. We engage with external researchers and credit valid reports."
      },
      "researcher": {
        "title": "In-house security researchers",
        "body": "We have security researchers on the team. Every system gets looked at from the attacker's side too."
      }
    }
  },
  "process": {
    "eyebrow": "How we work",
    "title": "Predictable process, documented deliveries.",
    "lead": "A flow designed for enterprise and public-sector projects — auditable, reliable, and surprise-free at every phase.",
    "steps": {
      "01": {
        "title": "Discovery",
        "body": "We listen before we propose. We map operations, business objectives, and technical constraints — including legal and compliance requirements when applicable."
      },
      "02": {
        "title": "Technical planning",
        "body": "Solution architecture, stack selection, delivery plan, and acceptance criteria. No black box — every technical decision is documented and justified."
      },
      "03": {
        "title": "Development & UAT",
        "body": "Incremental delivery, technical documentation, automated tests, and continuous review. You watch the system evolve from sprint one to deploy."
      },
      "04": {
        "title": "Operation, support & evolution",
        "body": "Hosting, monitoring, technical support, and continuous evolution — with a clear SLA throughout the contract."
      }
    }
  },
  "contact": {
    "eyebrow": "Contact",
    "title": "Let's talk about your project.",
    "lead": "Tell us about your company, the operational problem, or the agency's request — we'll come back with an initial technical opinion and a clear delivery path, no commitment.",
    "send_email": "Send email",
    "view_github": "View on GitHub",
    "labels": {
      "email": "Email",
      "github": "GitHub",
      "address": "Address",
      "cnpj": "Tax ID"
    }
  },
  "footer": {
    "company_legal": "Niertech Tecnologias e Sistemas LTDA · Tax ID 64.745.677/0001-31",
    "copyright": "© {{year}} Niertech Solutions",
    "city": "São Paulo, Brazil"
  },
  "whatsapp": {
    "aria": "Talk to us on WhatsApp",
    "message": "Hi! I'd like to know more about Niertech's services."
  },
  "lang_switcher": {
    "aria": "Select language",
    "pt-BR": "Português",
    "en": "English",
    "es": "Español"
  }
}
